import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router';

import { type AgentNavGroup } from '@/products/helpdesk/features/nav/agent-nav';
import { cn } from '@/lib/utils';
import { usePlatformSidebarCollapsed } from '@/platform/components/platform-sidebar-context';

function navInitial(label: string): string {
  const first = label.trim().charAt(0);
  return first ? first.toLocaleUpperCase('de-DE') : '?';
}

export function AgentNavList({
  groups,
  onNavigate,
  dense = false,
}: {
  groups: AgentNavGroup[];
  onNavigate?: () => void;
  dense?: boolean;
}) {
  const sidebarCollapsed = usePlatformSidebarCollapsed();

  return (
    <nav aria-label="Hauptnavigation" className="space-y-5">
      {groups.map((group) => (
        <AgentNavGroupBlock
          key={group.id}
          group={group}
          onNavigate={onNavigate}
          dense={dense}
          sidebarCollapsed={sidebarCollapsed}
        />
      ))}
    </nav>
  );
}

function AgentNavGroupBlock({
  group,
  onNavigate,
  dense,
  sidebarCollapsed,
}: {
  group: AgentNavGroup;
  onNavigate?: () => void;
  dense: boolean;
  sidebarCollapsed: boolean;
}) {
  const [open, setOpen] = useState(true);
  const labeled = Boolean(group.label);
  /** Collapsed rail clips labels; destinations stay mounted so width can animate without a swap. */
  const showItems = sidebarCollapsed || !labeled || open;
  const primary = !labeled;

  return (
    <div>
      {labeled && group.label ? (
        <button
          type="button"
          className="sidebar-hit flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase hover:bg-accent"
          aria-expanded={open}
          aria-label={open ? `${group.label} zuklappen` : `${group.label} aufklappen`}
          title={sidebarCollapsed ? group.label : undefined}
          onClick={() => {
            if (!sidebarCollapsed) {
              setOpen((value) => !value);
            }
          }}
        >
          <span
            aria-hidden
            className="sidebar-mark flex size-4 shrink-0 items-center justify-center text-[0.65rem] font-semibold tracking-wide"
          >
            {navInitial(group.label)}
          </span>
          <span className="sidebar-copy min-w-0 flex-1 truncate">{group.label}</span>
          <ChevronDownIcon
            aria-hidden
            className={cn(
              'sidebar-copy ml-auto size-3.5 shrink-0 transition-transform',
              !open && '-rotate-90',
            )}
          />
        </button>
      ) : null}
      {showItems ? (
        <ul className="space-y-0.5">
          {group.items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                aria-label={item.label}
                title={sidebarCollapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'sidebar-hit flex items-center gap-2 rounded-md px-3 text-sm whitespace-nowrap text-muted-foreground transition-colors hover:bg-accent hover:text-primary',
                    dense ? 'min-h-11' : 'min-h-9',
                    isActive && 'bg-accent font-medium text-primary',
                  )
                }
              >
                {primary && item.icon ? (
                  <span className="inline-flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
                    {item.icon}
                  </span>
                ) : (
                  <span
                    aria-hidden
                    className="sidebar-mark flex size-4 shrink-0 items-center justify-center text-[0.7rem] font-semibold tracking-wide"
                  >
                    {navInitial(item.label)}
                  </span>
                )}
                <span className="sidebar-copy truncate">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
